<template>
  <div
    class="c-graph c-graph--summary"
    :class="{ 'is-error': isError, 'is-loading': isLoading || isDrawing }"
    ref="canvas"
  >
    <Loading v-if="isLoading || isDrawing" transparent />
    <Error v-if="!isLoading && isError" transparent>
      <template slot="title">
        データがありません。
      </template>
      <ul>
        <li>
          <span class="c-text--accent">表示期間</span>を変更してください。
        </li>
        <li>
          初回連携時はデータの取得に<span class="c-text--accent"
            >最大48時間</span
          >かかるため、<br />時間を置いてご確認ください。
        </li>
      </ul>
    </Error>
    <svg class="c-graph__svg" ref="svg">
      <defs ref="defs">
        <GraphMask :size="size" />
      </defs>
      <g class="c-graph-label" ref="gLabel"></g>
      <g class="c-graph-bg" clip-path="url(#clipPath)">
        <g class="c-graph-bg-pointer" ref="gBgPointer"></g>
        <GraphBg
          :data="x.bg"
          direction="x"
          :scale="d3.x"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgX"
        />
        <GraphBg
          v-if="y.display.length"
          :data="y[y.display[0].name].bg"
          direction="y"
          :scale="d3.y[y.display[0].name]"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gBgY"
        />
      </g>
      <g
        class="c-graph-bar c-graph-bar--prev"
        clip-path="url(#clipPath)"
        ref="gBarPrev"
      ></g>
      <g class="c-graph-bar" clip-path="url(#clipPath)" ref="gBar"></g>
      <g
        class="c-graph-line c-graph-line--prev"
        clip-path="url(#clipPath)"
        ref="gLinePrev"
      ></g>
      <g class="c-graph-line" clip-path="url(#clipPath)" ref="gLine"></g>
      <g
        class="c-graph-marker c-graph-marker--prev"
        clip-path="url(#clipPath)"
        ref="gMarkerPrev"
      ></g>
      <g class="c-graph-marker" clip-path="url(#clipPath)" ref="gMarker"></g>
      <GraphFrame :size="size" hasLine />
      <g class="c-graph-pointer" clip-path="url(#clipPath)" ref="gPointer">
        <rect
          x="0"
          y="0"
          :height="size.canvasH"
          :width="size.canvasW"
          class="c-graph-pointer__area"
        />
      </g>
      <g class="c-graph-axis">
        <GraphAxis
          :data="x.axis"
          position="bottom"
          :scale="d3.x"
          :scaleType="x.scaleType"
          :size="size"
          :transition="transition"
          type="comparison"
          :isDrawed="isDrawed"
          ref="gAxisX"
        />
        <GraphAxis
          v-if="y.display.length"
          :data="y[y.display[0].name].axis"
          position="left"
          :scale="d3.y[y.display[0].name]"
          :scaleType="y[y.display[0].name].scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisYL"
        />
        <GraphAxis
          v-if="y.display.length > 1"
          :data="y[y.display[1].name].axis"
          position="right"
          :scale="d3.y[y.display[1].name]"
          :scaleType="y[y.display[1].name].scaleType"
          :size="size"
          :transition="transition"
          :isDrawed="isDrawed"
          ref="gAxisYR"
        />
      </g>
    </svg>
    <GraphTooltip
      :margin="36"
      :position="{ x: tooltip.x, y: tooltip.y }"
      :size="size"
      :isVisible="tooltip.isVisible && !isError ? true : false"
    >
      <template slot="title">
        {{ tooltip.data.current.date | dateText('YYYY年MM月') }}と{{
          tooltip.data.prev.date | dateText('YYYY年MM月')
        }}の比較
      </template>
      <ul v-if="checked.length" class="c-graph-tooltip-list--comparison">
        <li
          v-for="path in pathes"
          :key="path"
          class="c-graph-tooltip-list--comparison__item"
          :class="{ 'is-hidden': checked.indexOf(path) === -1 }"
        >
          <div class="c-graph-tooltip-list--comparison__key">
            {{ itemData[path].label }}
          </div>
          <div class="c-graph-tooltip-list--comparison__value">
            <p
              class="c-graph-tooltip-list--comparison__current"
              :class="[
                'c-graph-tooltip-list--comparison__current--' +
                  itemData[path].color,
                'c-graph-tooltip-list--comparison__current--' +
                  itemData[path].type
              ]"
            >
              {{ tooltip.data.current.date | dateText('YYYY年') }}：<template
                v-if="
                  tooltip.data.current[path] || tooltip.data.current[path] === 0
                "
                >{{
                  tooltip.data.current[path]
                    | roundFloatingPointNumber(2)
                    | separateNumber
                }}
              </template>
              <template v-else>
                -
              </template>
            </p>
            <p
              class="c-graph-tooltip-list--comparison__prev"
              :class="[
                'c-graph-tooltip-list--comparison__prev--' +
                  itemData[path].color,
                'c-graph-tooltip-list--comparison__prev--' + itemData[path].type
              ]"
            >
              {{ tooltip.data.prev.date | dateText('YYYY年') }}：<template
                v-if="tooltip.data.prev[path] || tooltip.data.prev[path] === 0"
                >{{
                  tooltip.data.prev[path]
                    | roundFloatingPointNumber(2)
                    | separateNumber
                }}
              </template>
              <template v-else>
                -
              </template>
            </p>
            <p class="c-graph-tooltip-list--comparison__comparison">
              前年比
              <template
                v-if="tooltip.data.current[path] && tooltip.data.prev[path]"
              >
                <template v-if="itemData[path].type === 'rank'">
                  {{
                    ((tooltip.data.prev[path] / tooltip.data.current[path]) *
                      100)
                      | roundFloatingPointNumber(1)
                  }}%
                </template>
                <template v-else>
                  {{
                    ((tooltip.data.current[path] / tooltip.data.prev[path]) *
                      100)
                      | roundFloatingPointNumber(1)
                  }}%
                </template>
              </template>
              <template v-else>
                -
              </template>
            </p>
          </div>
        </li>
      </ul>
    </GraphTooltip>
  </div>
</template>

<script>
import Error from '@/components/Error';
import GraphAxis from '@/components/GraphAxis';
import GraphBg from '@/components/GraphBg';
import GraphFrame from '@/components/GraphFrame';
import GraphMask from '@/components/GraphMask';
import GraphTooltip from '@/components/GraphTooltip';
import Loading from '@/components/Loading';
import utils from '@/utils';
import * as d3 from 'd3';

export default {
  props: ['checked', 'data', 'itemData', 'period', 'isLoading'],

  components: {
    Error,
    GraphAxis,
    GraphBg,
    GraphFrame,
    GraphMask,
    GraphTooltip,
    Loading
  },

  data() {
    return {
      bar: {
        data: {
          current: '',
          prev: ''
        },
        resetData: {
          current: '',
          prev: ''
        }
      },
      d3: {
        band: () => {},
        line: () => {},
        x: () => {},
        y: {}
      },
      el: {
        canvas: '',
        svg: '',
        gBar: {
          current: '',
          prev: ''
        },
        gBgPointer: '',
        gDefs: '',
        gLabel: '',
        gLine: {
          current: '',
          prev: ''
        },
        gMarker: {
          current: '',
          prev: ''
        },
        gPointer: ''
      },
      path: {
        data: {
          current: '',
          prev: ''
        },
        resetData: {
          current: '',
          prev: ''
        }
      },
      pointer: {
        data: {
          current: [],
          prev: []
        }
      },
      size: {
        axisXH: 40,
        axisYW: {
          left: 20,
          right: 20
        },
        barR: 3,
        barW: 11,
        canvasH: 0,
        canvasW: 0,
        frameW: 1,
        graphH: 0,
        graphW: 0,
        graph: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 0
        },
        markerR: 3.5,
        offset: {
          bottom: 0,
          left: 0,
          right: 0,
          top: 33
        },
        pointerMarkerR: 6,
        windowW: window.innerWidth
      },
      tooltip: {
        data: {
          current: {},
          prev: {}
        },
        x: 0,
        y: 0,
        isVisible: false
      },
      transition: {
        duration: 500,
        ease: d3.easeQuad
      },
      x: {
        axis: [],
        bg: [],
        count: 0,
        domain: [],
        scaleType: 'timeMonth'
      },
      y: {
        display: []
      },
      isDrawed: false,
      isDrawing: false
    };
  },

  computed: {
    pathes() {
      return Object.keys(this.itemData);
    },
    isError() {
      if (!this.data.current.length && !this.data.prev.length) {
        return true;
      }
      return false;
    }
  },

  watch: {
    checked() {
      if (this.isLoading || !this.isDrawed) {
        return;
      }
      this.y.display = this.getDisplayY();
      this.drawPath('current');
      this.drawPath('prev');
      this.drawAxisY();
      this.drawLabel();
      this.drawPointer();
    },
    data: {
      handler() {
        this.update();
      },
      deep: true
    }
  },

  mounted() {
    this.setEl();
    this.setSize();
    this.update();
    this.setEvent();
    window.addEventListener('resize', this.resize);
  },
  destroyed() {
    window.removeEventListener('resize', this.resize);
  },

  methods: {
    update() {
      if (this.isLoading) {
        return;
      }
      this.isDrawing = true;
      this.hideTooltip();
      this.setData();
      this.setScale();
      this.draw();
      this.isDrawed = true;
      this.isDrawing = false;
    },

    setEl() {
      this.el.canvas = this.$refs.canvas;
      this.el.svg = d3.select(this.$refs.svg);
      this.el.gBar.current = d3.select(this.$refs.gBar);
      this.el.gBar.prev = d3.select(this.$refs.gBarPrev);
      this.el.gBgPointer = d3.select(this.$refs.gBgPointer);
      this.el.gDefs = d3.select(this.$refs.defs);
      this.el.gLabel = d3.select(this.$refs.gLabel);
      this.el.gLine.current = d3.select(this.$refs.gLine);
      this.el.gLine.prev = d3.select(this.$refs.gLinePrev);
      this.el.gMarker.current = d3.select(this.$refs.gMarker);
      this.el.gMarker.prev = d3.select(this.$refs.gMarkerPrev);
      this.el.gPointer = d3.select(this.$refs.gPointer);
    },

    setSize() {
      this.size.canvasH = this.el.canvas.clientHeight;
      this.size.canvasW = this.el.canvas.clientWidth;

      this.size.graphH =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW * 2 -
        this.size.offset.top -
        this.size.offset.bottom;

      this.size.graphW =
        this.size.canvasW -
        this.size.axisYW.left -
        this.size.axisYW.right -
        this.size.frameW * 2 -
        this.size.offset.left -
        this.size.offset.right;

      this.size.graph.bottom =
        this.size.canvasH -
        this.size.axisXH -
        this.size.frameW -
        this.size.offset.bottom;

      this.size.graph.left =
        this.size.offset.left + this.size.axisYW.left + this.size.frameW;

      this.size.graph.right =
        this.size.canvasW -
        this.size.axisYW.right -
        this.size.frameW -
        this.size.offset.right;

      this.size.graph.top = this.size.offset.top + this.size.frameW;
    },

    setEvent() {
      this.el.gPointer
        .on('mousemove', this.mousemovePointer)
        .on('mouseleave', this.hideTooltip);
    },

    setData() {
      this.bar.data.current = [];
      this.bar.data.prev = [];
      this.bar.resetData.current = [];
      this.bar.resetData.prev = [];
      this.path.data.current = [];
      this.path.data.prev = [];
      this.path.resetData.current = [];
      this.path.resetData.prev = [];
      this.pointer.data.current = [];
      this.pointer.data.prev = [];

      // 各月の配列を作る
      const months = {
        current: d3.timeMonths(this.period.current[0], this.period.current[1]),
        prev: d3.timeMonths(this.period.prev[0], this.period.prev[1])
      };

      const items = this.getMonthlyData(
        'current',
        months.current,
        this.data.current
      );
      const prevItems = this.getMonthlyData(
        'prev',
        months.prev,
        this.data.prev
      );
      this.pointer.data.current = items;
      this.pointer.data.prev = prevItems;

      for (let i = 0; i < this.pathes.length; i++) {
        const path = this.pathes[i];
        const scaleType = this.itemData[path].type;
        const graphType = scaleType === 'number' ? 'bar' : 'path';
        const data = this.getPathData(items, path);
        const prevData = this.getPathData(prevItems, path);
        const resetData = this.getResetData(data, scaleType);
        const prevResetData = this.getResetData(prevData, scaleType);

        this[graphType].data.current.push(data);
        this[graphType].resetData.current.push(resetData);
        this[graphType].data.prev.push(prevData);
        this[graphType].resetData.prev.push(prevResetData);

        const axisY = this.getAxisY(data.concat(prevData), scaleType);
        const bgY = this.getBgY(axisY, scaleType);
        const domainY = this.getDomainY(axisY, scaleType);

        this.y[path] = {
          axis: axisY,
          bg: bgY,
          domain: domainY,
          scaleType: scaleType
        };
      }

      this.x.domain = [0, months.current.length - 1];
      this.x.count = months.current.length - 1;
      this.x.bg = d3.range(0, months.current.length);
      this.x.axis = months.current;

      this.y.display = this.getDisplayY();
    },

    getMonthlyData(term, months, data) {
      return months.map(date => {
        let obj = { date: date, term: term };
        for (let i = 0; i < this.pathes.length; i++) {
          obj[this.pathes[i]] = null;
        }
        const startTime = date.getTime();
        const endTime = utils.getEndDate(date, 'month').getTime();

        for (let i = 0; i < this.pathes.length; i++) {
          const path = this.pathes[i];
          let total = 0;
          for (let j = 0; j < data.length; j++) {
            const item = data[j];
            const time = utils.getDateResetHours(item.date).getTime();
            if (time >= startTime && time <= endTime) {
              if (!utils.isEmpty(item[path])) {
                obj[path] += item[path];
                total++;
              }
            } else if (time > endTime) {
              break;
            }
          }
          if (obj[path]) {
            if (this.itemData[path].type === 'percentage') {
              obj[path] = (obj[path] / total) * 100;
            } else if (this.itemData[path].type === 'rank') {
              obj[path] = obj[path] / total;
            }
          }
        }
        return obj;
      });
    },

    getPathData(data, path) {
      return data.map((item, i) => {
        return {
          path: path,
          date: item.date,
          term: item.term,
          value: item[path]
        };
      });
    },

    getResetData(data, type) {
      return data.map(item => {
        let obj = Object.assign({}, item);
        if (!utils.isEmpty(obj.value)) {
          // 順位だけ1番下の値が50なので
          if (type === 'rank') {
            obj.value = 50;
          } else {
            obj.value = 0;
          }
        }
        return obj;
      });
    },

    getAxisY(data, scaleType) {
      if (scaleType === 'rank') {
        return [0, 1, 25, 50];
      } else {
        let max =
          d3.max(
            data.map(item => {
              return item.value;
            })
          ) || 0;
        if (scaleType === 'percentage') {
          return utils.getTickValues(5, Math.max(max, 100), 25);
        } else {
          return utils.getTickValues(5, max + 1);
        }
      }
    },

    getBgY(axis, scaleType) {
      if (scaleType === 'rank') {
        return d3.range(axis[2] / 4, axis[axis.length - 1], axis[2] / 4);
      } else {
        return d3.range(axis[1] / 2, axis[axis.length - 1], axis[1] / 2);
      }
    },

    getDomainY(axis, scaleType) {
      if (scaleType === 'rank') {
        return [axis[axis.length - 1], 0];
      } else {
        return [0, axis[axis.length - 1]];
      }
    },

    getDisplayY() {
      let array = [];
      if (this.checked.length && this.checked.length < 3) {
        for (let i = 0; i < this.checked.length; i++) {
          let item = this.itemData[this.checked[i]];
          item.name = this.checked[i];
          array.push(item);
        }
      }
      return array;
    },

    setScale() {
      this.d3.band = d3
        .scaleBand()
        .domain(this.x.bg)
        .range([this.size.graph.left, this.size.graph.right]);

      this.d3.x = d3
        .scaleLinear()
        .domain(this.x.domain)
        .rangeRound([
          this.size.graph.left + this.d3.band.bandwidth() / 2,
          this.size.graph.right - this.d3.band.bandwidth() / 2
        ]);

      for (let i = 0; i < this.pathes.length; i++) {
        this.d3.y[this.pathes[i]] = d3
          .scaleLinear()
          .domain(this.y[this.pathes[i]].domain)
          .rangeRound([this.size.graph.bottom, this.size.graph.top]);

        this.d3.line[this.pathes[i]] = this.getD3Line(
          this.d3.y[this.pathes[i]]
        );
      }
    },

    getD3Line(scaleY) {
      return d3
        .line()
        .defined(d => {
          return d.value !== null;
        })
        .x((d, i) => {
          return this.d3.x(i);
        })
        .y((d, i) => {
          return scaleY(d.value);
        });
    },

    draw() {
      this.drawPath('current');
      this.drawPath('prev');
      this.$refs.gAxisX.draw();
      this.$refs.gBgX.draw();
      this.drawAxisY();
      this.drawLabel();
      this.drawPointer();
    },

    attrBarGroup(selection) {
      selection.attr('transform', (d, i) => {
        return `translate(${-28 + 32 * i}, 0)`;
      });
    },

    attrBar(selection) {
      selection
        .attr('opacity', (d, i) => {
          if (this.checked.indexOf(d.path) === -1) {
            return 0;
          } else {
            return 1;
          }
        })
        .attr('x', (d, i) => {
          let x = this.d3.x(i);
          if (d.term === 'current') {
            x += this.size.barW + 3;
          }
          return x;
        })
        .attr('y', (d, i) => {
          if (this.checked.indexOf(d.path) === -1) {
            return this.d3.y[d.path](0);
          } else {
            return this.d3.y[d.path](d.value);
          }
        })
        .attr('width', this.size.barW)
        .attr('height', (d, i) => {
          let h = this.d3.y[d.path](0);
          if (this.checked.indexOf(d.path) !== -1) {
            h -= this.d3.y[d.path](d.value);
          }
          return h + this.size.barR;
        })
        .attr('rx', this.size.barR)
        .attr('ry', this.size.barR);
    },

    attrLine(selection) {
      selection
        .attr('opacity', (d, i) => {
          if (this.checked.indexOf(d[i].path) === -1) {
            return 0;
          } else {
            return 1;
          }
        })
        .attr('d', (d, i) => {
          if (this.checked.indexOf(d[i].path) === -1) {
            return this.d3.line[d[i].path](this.path.resetData[d[i].term][i]);
          } else {
            return this.d3.line[d[i].path](d);
          }
        });
    },

    attrMarker(selection) {
      selection
        .attr('opacity', (d, i, nodes) => {
          let index = nodes[i].getAttribute('data-index');
          let data = this.path.data[d.term][index];
          if (!utils.isEmpty(d.value) && this.checked.indexOf(d.path) !== -1) {
            if (
              (!data[i - 1] || utils.isEmpty(data[i - 1].value)) &&
              (!data[i + 1] || utils.isEmpty(data[i + 1].value))
            ) {
              return 1;
            }
          }
          return 0;
        })
        .attr('cx', (d, i) => {
          return this.d3.x(i);
        })
        .attr('cy', (d, i) => {
          if (this.checked.indexOf(d.path) === -1) {
            return this.d3.y[d.path](0);
          } else {
            return this.d3.y[d.path](d.value);
          }
        });
    },

    drawPath(term) {
      let transition = d3
        .transition()
        .duration(this.transition.duration)
        .ease(this.transition.ease);

      this.el.gBar[term]
        .selectAll('g')
        .data(this.bar.data[term])
        .join(
          enter => {
            return enter.append('g').call(this.attrBarGroup);
          },
          update => {
            return update.call(this.attrBarGroup);
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          d3.select(nodes[index])
            .selectAll('rect')
            .data(this.bar.data[term][index])
            .join(
              enter => {
                return enter
                  .append('rect')
                  .attr('class', (d, i) => {
                    const color = this.itemData[d.path].color;
                    return `c-color-fill--${color}`;
                  })
                  .call(enter =>
                    enter
                      .data(this.bar.resetData[term][index])
                      .call(this.attrBar)
                      .data(this.bar.data[term][index])
                      .transition(transition)
                      .call(this.attrBar)
                  );
              },
              update => {
                return update
                  .interrupt()
                  .transition(transition)
                  .call(this.attrBar);
              },
              exit => {
                return exit.remove();
              }
            );
        });

      this.el.gLine[term]
        .selectAll('path')
        .data(this.path.data[term])
        .join(
          enter => {
            return enter
              .append('path')
              .attr('class', (d, i) => {
                const color = this.itemData[this.pathes[i + 2]].color;
                return `c-graph-line__path c-color-stroke--${color}`;
              })
              .call(enter =>
                enter
                  .data(this.path.resetData[term])
                  .call(this.attrLine)
                  .data(this.path.data[term])
                  .transition(transition)
                  .call(this.attrLine)
              );
          },
          update => {
            return update
              .interrupt()
              .transition(transition)
              .call(this.attrLine);
          },
          exit => {
            return exit.remove();
          }
        );

      this.el.gMarker[term]
        .selectAll('g')
        .data(this.path.data[term])
        .join(
          enter => {
            return enter.append('g');
          },
          update => {
            return update;
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          d3.select(nodes[index])
            .selectAll('circle')
            .data(this.path.data[term][index])
            .join(
              enter => {
                return enter
                  .append('circle')
                  .attr('class', (d, i) => {
                    const color = this.itemData[d.path].color;
                    return `c-graph-marker__circle c-color-stroke--${color}`;
                  })
                  .attr('r', this.size.markerR)
                  .attr('data-index', index)
                  .call(enter => {
                    enter
                      .data(this.path.resetData[term][index])
                      .call(this.attrMarker)
                      .data(this.path.data[term][index])
                      .transition(transition)
                      .call(this.attrMarker);
                  });
              },
              update => {
                return update
                  .interrupt()
                  .transition(transition)
                  .call(this.attrMarker);
              },
              exit => {
                return exit.remove();
              }
            );
        });
    },

    drawAxisY() {
      if (this.$refs.gAxisYL) {
        this.$refs.gAxisYL.draw();
      }
      if (this.$refs.gAxisYR) {
        this.$refs.gAxisYR.draw();
      }
      if (this.$refs.gBgY) {
        this.$refs.gBgY.draw();
      }
    },

    drawLabel() {
      let attrLabel = selection => {
        selection
          .text((d, i) => {
            return d.label;
          })
          .attr('x', (d, i) => {
            return i === 0 ? 0 : this.size.canvasW;
          })
          .attr('y', 12)
          .attr('text-anchor', (d, i) => {
            return i === 0 ? 'start' : 'end';
          });
      };

      this.el.gLabel
        .selectAll('text')
        .data(this.y.display)
        .join(
          enter => {
            return enter.append('text').call(enter =>
              enter
                .attr('opacity', 0)
                .call(attrLabel)
                .transition()
                .duration(this.transition.duration)
                .ease(this.transition.ease)
                .attr('opacity', 1)
            );
          },
          update => {
            return update
              .interrupt()
              .attr('opacity', 0)
              .call(attrLabel)
              .transition()
              .duration(this.transition.duration)
              .ease(this.transition.ease)
              .attr('opacity', 1);
          },
          exit => {
            return exit.remove();
          }
        );
    },

    attrPointer(selection) {
      selection.attr('transform', (d, i) => {
        return `translate(${this.d3.x(i)}, 0)`;
      });
    },

    attrPointerMarker(selection) {
      selection
        .attr('class', (d, i) => {
          return `c-graph-pointer__marker`;
        })
        .attr('display', (d, i) => {
          if (utils.isEmpty(d.value) || this.checked.indexOf(d.path) == -1) {
            return 'none';
          }
        })
        .attr('cx', (d, i) => {
          if (d.path === 'impressions') {
            return -9;
          } else if (d.path === 'clicks') {
            return -9 + 32;
          }
          return 0;
        })
        .attr('cy', (d, i) => {
          return this.d3.y[d.path](d.value);
        })
        .attr('r', this.size.pointerMarkerR);
    },

    attrBgPointer(selection) {
      selection
        .attr('x', (d, i) => {
          return Math.ceil(this.d3.band(i));
        })
        .attr('y', this.size.graph.top)
        .attr('height', this.size.graphH)
        .attr('width', (d, i) => {
          return Math.ceil(this.d3.band.bandwidth());
        });
    },

    drawPointer() {
      this.el.gPointer
        .selectAll('g')
        .data(this.pointer.data.current)
        .join(
          enter => {
            return enter.append('g').call(this.attrPointer);
          },
          update => {
            return update.call(this.attrPointer);
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, index, nodes) => {
          let data = this.pathes.map(path => {
            return {
              date: d.date,
              path: path,
              value: d[path]
            };
          });

          d3.select(nodes[index])
            .selectAll('circle')
            .data(data)
            .join(
              enter => {
                return enter.append('circle').call(this.attrPointerMarker);
              },
              update => {
                return update.call(this.attrPointerMarker);
              },
              exit => {
                return exit.remove();
              }
            );
        });

      this.el.gBgPointer
        .selectAll('rect')
        .data(this.pointer.data.current)
        .join(
          enter => {
            return enter
              .append('rect')
              .attr('class', 'c-graph-bg-pointer__rect')
              .call(this.attrBgPointer);
          },
          update => {
            return update.call(this.attrBgPointer);
          },
          exit => {
            return exit.remove();
          }
        );
    },

    mousemovePointer() {
      if (!this.isDrawed) {
        return;
      }
      let pointerIndex = Math.round(this.d3.x.invert(d3.event.offsetX));
      if (pointerIndex < 0) {
        pointerIndex = 0;
      } else if (pointerIndex > this.x.count) {
        pointerIndex = this.x.count;
      }
      let pointerData = {
        current: this.pointer.data.current[pointerIndex],
        prev: this.pointer.data.prev[pointerIndex]
      };

      this.el.gPointer
        .selectAll('g')
        .classed('is-hover', (d, i) => i === pointerIndex);
      this.el.gBgPointer
        .selectAll('rect')
        .classed('is-hover', (d, i) => i === pointerIndex);

      if (pointerData.current && pointerData.prev) {
        this.showTooltip(
          pointerData,
          this.d3.x(pointerIndex),
          d3.event.offsetY
        );
      } else {
        this.hideTooltip();
      }
    },

    showTooltip(d, x, y) {
      this.tooltip.data = d;
      this.tooltip.x = x;
      this.tooltip.y = y;
      this.tooltip.isVisible = true;
    },
    hideTooltip() {
      this.tooltip.isVisible = false;
      this.el.gPointer.selectAll('g').classed('is-hover', false);
      this.el.gBgPointer.selectAll('rect').classed('is-hover', false);
    },

    resize() {
      this.setSize();

      if (!this.isDrawed) {
        return;
      }
      if (this.size.windowW !== window.innerWidth) {
        this.size.windowW = window.innerWidth;
        this.setScale();

        this.resizePath('current');
        this.resizePath('prev');

        this.el.gPointer.selectAll('g').call(this.attrPointer);
        this.el.gBgPointer.selectAll('rect').call(this.attrBgPointer);

        this.$refs.gAxisX.resize();
        this.$refs.gBgX.resize();

        if (this.$refs.gAxisYR) {
          this.$refs.gAxisYR.resize();
        }
        if (this.$refs.gBgY) {
          this.$refs.gBgY.resize();
        }

        this.el.gLabel.selectAll('text').attr('x', (d, i) => {
          return i === 0 ? 0 : this.size.canvasW;
        });
      }
    },
    resizePath(term) {
      this.el.gBar[term]
        .selectAll('g')
        .call(this.attrBarGroup)
        .selectAll('rect')
        .call(this.attrBar);
      this.el.gLine[term].selectAll('path').call(this.attrLine);
      this.el.gMarker[term]
        .selectAll('g')
        .selectAll('circle')
        .call(this.attrMarker);
    }
  }
};
</script>
